/**
 * Visits to the front page and the manual, counted with Google Analytics: on the live site only (a production build
 * served from SITE_HOST), never in the editor or the projects page, and without cookies: Google's consent mode is set
 * to "denied" for storage and ads before anything is sent, so GA sends anonymous pings and nothing is kept on the
 * visitor's computer (no consent banner needed). The script loads the first time a counted page is shown; nothing
 * about anyone's projects or footage is ever sent.
 */
import type { Page } from './nav';

const ID = 'G-1WC5F4TNMN';
/** Where the live site is: anywhere else (a dev server, a preview) counts nothing. */
const SITE_HOST = 'cutroom.indieduo.in';

type Gtag = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

/** The pages counted (the editor and the projects page are not). */
export const counted = (page: Page) => page === 'landing' || page === 'help';

/** Whether this is the live site, built for it. */
const live = () => import.meta.env.PROD && location.hostname === SITE_HOST;

let started = false;
function start(): Gtag {
  if (!started) {
    started = true;
    window.dataLayer = window.dataLayer ?? [];
    // (gtag's queue takes the arguments object itself, as Google's snippet does)
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
    window.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    window.gtag('js', new Date());
    // page views are sent by hand, for the counted pages only
    window.gtag('config', ID, { send_page_view: false });
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${ID}`;
    document.head.appendChild(s);
  }
  return window.gtag!;
}

/** A page shown: counted when it's the front page or the manual, on the live site. */
export function pageShown(page: Page) {
  if (!live() || !counted(page)) return;
  start()('event', 'page_view', {
    page_title: document.title,
    page_location: location.origin + location.pathname + location.hash,
  });
}
